import { Link } from "react-router-dom";
import { Award, Heart, Truck, Star, ExternalLink, Play } from "lucide-react";

const VAN_URL = "https://customer-assets.emergentagent.com/job_barber-membership-1/artifacts/rvy2rpaa_20210411_153748.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_barber-membership-1/artifacts/lnz6qr8g_Screenshot_20260112_182038_Drive.jpg";

const milestones = [
  { year: "2020", title: "LUXGROOM Launches", desc: "Born during the pandemic, bringing safe, private grooming to clients who needed it most." },
  { year: "2021", title: "The Van Arrives", desc: "Custom-fitted Mercedes Sprinter van becomes a full mobile barbershop on wheels." },
  { year: "2022", title: "Community Impact", desc: "Partnered with Felton Institute to provide free haircuts to unhoused individuals and shelter residents." },
  { year: "2023", title: "CBS News Feature", desc: "Featured on CBS News San Francisco for innovative mobile barbershop concept and community outreach." },
  { year: "2024", title: "Membership Launch", desc: "Introduced membership plans to give busy professionals consistent, premium grooming on their schedule." },
  { year: "2025", title: "Growing Strong", desc: "500+ active members across the Bay Area. Same mission: elite grooming, delivered." },
];

const values = [
  { icon: Award, title: "Craftsmanship", desc: "Every cut is a craft. We take pride in precision, consistency, and attention to detail that sets us apart." },
  { icon: Heart, title: "Community", desc: "From free haircuts for the unhoused to supporting local shelters, giving back is in our DNA." },
  { icon: Truck, title: "Convenience", desc: "Your time is valuable. We eliminate the commute, the wait, and the hassle — grooming comes to you." },
  { icon: Star, title: "Excellence", desc: "Premium tools, premium products, premium experience. Every single time. No exceptions." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-[#0A0A0B]">
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#C5A059]" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                  Our Story
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-[#F3F4F6] leading-[1.1] mb-6">
                Redefining the<br />
                <span className="text-[#C5A059]">customer experience.</span>
              </h1>
              <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-6">
                LUXGROOM Mobile Barber Technologies was founded by Lennotch "Notch" Taplett 
                with a simple vision: bring the barbershop to the client. What started during 
                the 2020 pandemic as a way to provide safe, private grooming has grown into the 
                Bay Area's premier mobile barber service.
              </p>
              <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-8">
                Our custom-fitted Mercedes Sprinter van is a fully equipped barbershop — complete with 
                hot and cold water, shampoo station, climate control, privacy, and even refreshments. 
                It's not just a haircut. It's an experience.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/membership"
                  data-testid="about-cta-membership"
                  className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
                >
                  View Memberships
                </Link>
                <a
                  href="https://www.cbsnews.com/sanfrancisco/news/mobile-barbershop-luxgroom-barber-san-francisco-lennotch-taplett/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cbs-link"
                  className="bg-transparent border border-white/20 text-white hover:border-[#C5A059] hover:bg-white/5 transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider flex items-center gap-2"
                >
                  <Play className="w-4 h-4" /> CBS News Feature
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src={VAN_URL}
                alt="LUXGROOM Mobile Barber Van"
                data-testid="about-van-image"
                className="rounded-md w-full object-cover shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#C5A059] text-black px-6 py-3 rounded-sm">
                <p className="font-heading text-lg font-medium">Est. 2020</p>
                <p className="text-xs">San Francisco, CA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                What Drives Us
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6]">
              Our values
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} data-testid={`value-${i}`} className="p-8 rounded-md bg-[#141416] border border-white/5 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center mb-5">
                  <v.icon className="w-6 h-6 text-[#C5A059]" />
                </div>
                <h3 className="font-heading text-lg font-medium text-[#F3F4F6] mb-2">{v.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed font-light">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-24 md:py-32 bg-[#141416]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                Giving Back
              </span>
              <div className="h-px w-12 bg-[#C5A059]" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-6">
              More than just haircuts
            </h2>
            <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-6">
              LUXGROOM proudly partners with the <strong className="text-[#F3F4F6]">Felton Institute</strong> to 
              provide free haircuts to unhoused individuals and shelter residents across San Francisco. 
              A fresh haircut can boost confidence, dignity, and help people on their journey toward 
              housing and stability.
            </p>
            <p className="text-sm text-[#71717A] italic">
              "A haircut is more than grooming — it's about making someone feel seen and valued."
              <span className="block mt-1 text-[#C5A059] not-italic font-medium">— Notch Taplett, Founder</span>
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                The Journey
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6]">
              Milestones
            </h2>
          </div>
          <div className="space-y-6 max-w-3xl">
            {milestones.map((m, i) => (
              <div key={i} data-testid={`milestone-${i}`} className="flex gap-6 items-start group">
                <div className="flex-shrink-0 w-16 text-right">
                  <span className="font-heading text-lg font-medium text-[#C5A059]">{m.year}</span>
                </div>
                <div className="relative flex-shrink-0 w-px bg-white/10 self-stretch">
                  <div className="absolute top-1.5 -left-1.5 w-3 h-3 rounded-full bg-[#C5A059] group-hover:scale-125 transition-transform" />
                </div>
                <div className="pb-6">
                  <h3 className="font-heading text-lg font-medium text-[#F3F4F6] mb-1">{m.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed font-light">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24 text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-4">
            Ready to experience LUXGROOM?
          </h2>
          <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-8">
            Join hundreds of Bay Area professionals who've made the switch to mobile grooming.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/membership"
              data-testid="about-bottom-cta"
              className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
            >
              View Memberships
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border border-white/20 text-white hover:border-[#C5A059] hover:bg-white/5 transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
